import { UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { PortalStyleConfig, ThemeColorSchemas, UnleashFeatures } from '@nx-frontend/shared/types';
import { ChatbotMessageComponent } from './chatbot-message';
import { ChatbotPromptComponent } from './chatbot-prompt';
import { $aiThreads, $writingMessage } from './store';
import { ChatbotMessage } from './types';

interface ChatbotChatProps {
  threadId: string | null;
  onSendMessage: (value: string) => void;
  aiName: string;
  aiCustomLogoUrl: string | null;
  isWriting: boolean;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onAddMessageToPage: (text: string, threadId: string) => void;
  onFeedback: (threadId: string, message: ChatbotMessage) => void;
  allowCreatePage?: boolean;
  disableLoading?: boolean;
  isAgent?: boolean;
}

export function ChatbotChat({
  threadId,
  onSendMessage,
  themeColors,
  themeStyle,
  aiName,
  aiCustomLogoUrl,
  isWriting,
  onAddMessageToPage,
  onFeedback,
  allowCreatePage = true,
  disableLoading,
  isAgent,
}: ChatbotChatProps) {
  const [aiThreads, writingMessage, unleashFlags] = useUnit([$aiThreads, $writingMessage, $unleashFlags]);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const [autoScrollOnWriting, setAutoScrollOnWriting] = useState(true);

  const displayMessageContext = unleashFlags[UnleashFeatures.AI_ASSISTANT_DETECT_USER_MESSAGE_CONTEXT];

  const messages = useMemo(() => {
    return threadId && aiThreads.threads?.[threadId]
      ? aiThreads.threads?.[threadId].messages || []
      : aiThreads.tmpMessages || [];
  }, [aiThreads.threads, aiThreads.tmpMessages, threadId]);
  const [loading, setLoading] = useState(!disableLoading && !messages.length);

  const aiWritingMessage = useMemo(() => {
    if (writingMessage !== null) {
      const { threadId: writingMessageThreadId, ...message } = writingMessage;
      if (!threadId || !writingMessageThreadId || writingMessageThreadId === threadId) {
        return message;
      }
    }
    return false;
  }, [threadId, writingMessage]);

  const t = useTranslations('features');

  const suggestions = threadId && aiThreads.threads?.[threadId] ? aiThreads.threads?.[threadId].suggestions : undefined;

  const scrollToBottom = useCallback(({ behavior }: { behavior?: 'smooth' } = {}) => {
    if (messagesRef.current !== null) {
      const node = messagesRef.current;
      node.scrollTo({
        top: node.scrollHeight,
        behavior,
      });
    }
  }, []);

  const onScrollMessages = useCallback(
    (event: UIEvent) => {
      const target = event.target as HTMLDivElement;
      if (isWriting) {
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
          if (!autoScrollOnWriting) {
            setAutoScrollOnWriting(true);
          }
        } else {
          if (autoScrollOnWriting) {
            setAutoScrollOnWriting(false);
          }
        }
      }
    },
    [autoScrollOnWriting, isWriting]
  );

  /* Scroll to end of chat on mount, load messages, add new messages */
  useEffect(() => {
    if (loading && messages.length) {
      setLoading(false);
    }
  }, [messages.length, loading]);

  useEffect(() => {
    if (!loading) {
      scrollToBottom();
    }
  }, [scrollToBottom, loading]);

  /* Set autoscroll flag on start AI writing */
  useEffect(() => {
    if (isWriting) {
      setAutoScrollOnWriting(true);
    } else {
      setAutoScrollOnWriting(false);
    }
  }, [isWriting]);

  /* On AI writing scroll */
  useEffect(() => {
    if (autoScrollOnWriting && isWriting) {
      scrollToBottom();
    }
  }, [autoScrollOnWriting, scrollToBottom, aiWritingMessage, isWriting]);

  /* On end AI writing scroll to bottom if available */
  useEffect(() => {
    if (autoScrollOnWriting && !isWriting) {
      scrollToBottom({ behavior: 'smooth' });
    }
  }, [autoScrollOnWriting, isWriting, scrollToBottom]);

  const onAddToPage = useCallback(
    (text: string) => {
      if (threadId) {
        onAddMessageToPage(text, threadId);
      }
    },
    [threadId, onAddMessageToPage]
  );

  const onMessageFeedback = useCallback(
    (message: ChatbotMessage) => {
      if (threadId) {
        onFeedback(threadId, message);
      }
    },
    [onFeedback, threadId]
  );

  return (
    <div ref={messagesRef} className="overflow-y-auto grow p-6 overflow-x-hidden" onScroll={onScrollMessages}>
      {loading ? (
        <div className="flex items-center justify-center w-full h-full">
          <Spinner width={42} height={42} />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-9">
            {messages.map((message, index) => (
              <ChatbotMessageComponent
                displayMessageContext={displayMessageContext}
                key={'message-' + threadId + '-' + index}
                message={message}
                themeColors={themeColors}
                themeStyle={themeStyle}
                isWriting={index === messages.length - 1 && isWriting}
                aiName={aiName}
                aiCustomLogoUrl={aiCustomLogoUrl}
                isLastMessage={!writingMessage && index === messages.length - 1}
                onAddMessageToPage={onAddToPage}
                onFeedback={onMessageFeedback}
                allowCreatePage={allowCreatePage}
                disliked={Boolean(
                  threadId && message.id && aiThreads.threads[threadId].dislikeMessages?.includes(message.id)
                )}
                isAgent={isAgent}
              />
            ))}
            {aiWritingMessage ? (
              <ChatbotMessageComponent
                key={'message-' + threadId + '-writing-message'}
                message={aiWritingMessage}
                themeColors={themeColors}
                themeStyle={themeStyle}
                isWriting={true}
                aiName={aiName}
                aiCustomLogoUrl={aiCustomLogoUrl}
                isLastMessage={true}
                isAgent={isAgent}
              />
            ) : null}
          </div>
          {!isWriting && suggestions && suggestions.length ? (
            <div className="flex flex-col w-full gap-0 mt-0 pt-2" ref={suggestionsRef}>
              <h2 className="font-semibold text-sm mb-1 mt-0">{t('ai-helper.related_for_you')}</h2>
              <div className="chatbot-suggestions flex flex-col w-full gap-1">
                {suggestions.slice(0, 5).map((suggestion, index) => (
                  <ChatbotPromptComponent
                    key={'suggestion-' + threadId + '-' + index}
                    text={suggestion}
                    onClick={onSendMessage}
                    promptType="chatSuggestion"
                  />
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
